import React, { useState, useEffect } from "react"
import { Layout, Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import {
  faAnglesLeft,
  faAnglesRight,
  faArrowLeft,
  faBook,
  faCirclePlay,
  faCog,
  faFolder,
} from "@fortawesome/free-solid-svg-icons"
import { useNavigate } from "react-router-dom"
import { useKindeAuth } from "@kinde-oss/kinde-auth-react"
import { KindeRole, KindeRolesClaim } from "types/kinde.types"
import { USER_ROLE } from "types/userRole.types"
import { useUiStore } from "stores/uiStore"
import { useProjectNavStore } from "stores/projectNavStore"
import { useProjectStore } from "stores/projectStore"
import { useUserStore } from "stores/userStore"
import {
  listTargetFor,
  openFolderInList,
} from "features/Projects/utils/projectListState"
import ProjectSectionNav from "./ProjectSectionNav.component"

// Define the props interface
interface SideMenuProps {
  current?: string[]
}

/** One app destination in the sidebar. */
interface AppItem {
  path: string
  label: string
  icon: IconDefinition
}

/** Sidebar width when open, with names. */
const OPEN_WIDTH = 160
/** Sidebar width when folded to icons: the narrowest it goes. */
const FOLDED_WIDTH = 48

interface SidebarRowProps {
  label: string
  icon: IconDefinition
  collapsed: boolean
  active?: boolean
  onClick: () => void
}

/**
 * One sidebar row: icon and name, or the icon alone with the name in a
 * tooltip when the sidebar is folded.
 *
 * @param props - The props for the component
 * @returns The row
 */
const SidebarRow: React.FC<SidebarRowProps> = ({
  label,
  icon,
  collapsed,
  active = false,
  onClick,
}) => {
  const row = (
    <button
      type="button"
      onClick={onClick}
      aria-label={collapsed ? label : undefined}
      aria-current={active ? "page" : undefined}
      className={`sidebar-row ${active ? "sidebar-row--active" : ""} ${
        collapsed ? "justify-center" : ""
      }`}
    >
      <FontAwesomeIcon className="sidebar-row-icon" icon={icon} fixedWidth />
      {!collapsed && <span className="truncate">{label}</span>}
    </button>
  )
  return collapsed ? (
    <Tooltip title={label} placement="right">
      {row}
    </Tooltip>
  ) : (
    row
  )
}

/**
 * SideMenu - the app's one sidebar.
 *
 * Outside a project it lists the app's places (Projects, Automation, Docs and,
 * for super admins, Settings). Inside a project (ProjectDetailsTabs has
 * published its sections to projectNavStore) it becomes that project's
 * section list, with "All projects" on top and the app's places at the
 * bottom. It folds to a 48px icon strip; the choice persists (uiStore).
 *
 * @param props - The props for the component
 * @returns The sidebar
 */
const SideMenu: React.FC<SideMenuProps> = ({ current }) => {
  const { getClaim } = useKindeAuth()
  const [roles, setRoles] = useState<KindeRole[]>([])

  const siderCollapsed = useUiStore((state) => state.siderCollapsed)
  const toggleSider = useUiStore((state) => state.toggleSider)
  const sections = useProjectNavStore((state) => state.sections)
  const project = useProjectStore((state) => state.project)
  const projectName = project.Name
  const username = useUserStore((state) => state.user.username)

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const userRoles = (await getClaim("roles")) as KindeRolesClaim | null
        setRoles(userRoles?.value || [])
      } catch (error) {
        console.error("Failed to load user roles:", error)
        setRoles([])
      }
    }
    loadRoles()
  }, [getClaim])

  const { Sider } = Layout
  const navigate = useNavigate()

  const isSuperAdmin = roles.some(
    (role: KindeRole) => role.key === USER_ROLE.SUPER_ADMIN,
  )

  // Projects only appears outside a project: inside one, "All projects" leads there.
  const projectsItem: AppItem = { path: "/home", label: "Projects", icon: faFolder }
  const appItems: AppItem[] = [
    { path: "/automation", label: "Automation", icon: faCirclePlay },
    { path: "/docs", label: "Docs", icon: faBook },
    ...(isSuperAdmin
      ? [{ path: "/superadmin", label: "Settings", icon: faCog }]
      : []),
  ]
  const inProject = sections !== null

  const renderAppItem = (item: AppItem) => (
    <SidebarRow
      key={item.path}
      label={item.label}
      icon={item.icon}
      collapsed={siderCollapsed}
      active={!inProject && (current ?? []).includes(item.path)}
      onClick={() => navigate(item.path)}
    />
  )

  return (
    <div className="sidebar">
      <Sider
        width={OPEN_WIDTH}
        collapsible
        trigger={null}
        collapsedWidth={FOLDED_WIDTH}
        collapsed={siderCollapsed}
      >
        <nav
          aria-label="Main navigation"
          className="flex flex-col h-full px-1.5 py-2 gap-0.5"
        >
          {inProject ? (
            <>
              <SidebarRow
                label="All projects"
                icon={faArrowLeft}
                collapsed={siderCollapsed}
                // Like the breadcrumb's "Projects": the list opens at
                // this project's folder.
                onClick={() =>
                  openFolderInList(navigate, listTargetFor(project, username))
                }
              />
              <div className="sidebar-divider" />
              {!siderCollapsed && (
                <div className="sidebar-heading" title={projectName}>
                  {projectName}
                </div>
              )}
              <ProjectSectionNav collapsed={siderCollapsed} />
              <div className="flex-1" />
              <div className="sidebar-divider" />
              {appItems.map(renderAppItem)}
            </>
          ) : (
            <>
              {[projectsItem, ...appItems].map(renderAppItem)}
              <div className="flex-1" />
            </>
          )}
          <SidebarRow
            label={siderCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            icon={siderCollapsed ? faAnglesRight : faAnglesLeft}
            collapsed={siderCollapsed}
            onClick={toggleSider}
          />
        </nav>
      </Sider>
    </div>
  )
}

export default SideMenu
